import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { AdminResponse } from '../../models/super-admin/adminResponse';
import { AdminState } from '../../models/enums/adminState';
import { SuperAdminService } from '../../services/super-admin/super-admin.service';
import { MessageService } from 'primeng/api';
import { DropdownModule } from 'primeng/dropdown';
import { Gender, GenderDescriptions } from '../../models/enums/enums';
import { DialogModule } from 'primeng/dialog';
import { FileSelectEvent, FileUpload, FileUploadModule } from 'primeng/fileupload';
import { RegisterAdminRequest } from '../../models/super-admin/registerAdminRequest';
import { ToggleButtonChangeEvent, ToggleButtonModule } from 'primeng/togglebutton';
import { GetAdminRequest } from '../../models/super-admin/getAdminRequest';

interface DropdownOption {
  name: string;
  value: AdminState;
}

@Component({
  selector: 'app-admin-management',
  standalone: true,
  imports: [CalendarModule,ReactiveFormsModule,FileUploadModule,DialogModule,CommonModule, FormsModule, ToggleButtonModule,InputTextModule, TableModule, ButtonModule, DropdownModule],
  templateUrl: './admin-management.component.html',
  styleUrl: './admin-management.component.scss'
})
export class AdminManagementComponent implements OnInit {

  @ViewChild('fileUploader') fileUploader!: FileUpload;

  totalRecords: number = 0;
  first: number = 0;
  rowSize: number = 10;
  pageSize = 10;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  adminState: AdminState | null  = null;
  adminStateOptions: DropdownOption[] = [];
  adminList: AdminResponse[] = [];
  adminToEdit: AdminResponse |null = null;
  readonly AdminState = AdminState;

  newAdminForm!: FormGroup;
  filterForm!: FormGroup;
  genders: { name: string; value: number }[] = [];
  attachment: File | undefined;

  isLoading: boolean = false;
  isVisible: boolean = false;
  submitted: boolean = false;
  checked: boolean = false;

  

  initializeForm(){
    this.filterForm = this.formBuilder.group({
      fromDate: [null],
      toDate: [null],
      organization: [null],
      adminState: [null]
    });

    this.newAdminForm = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      gender:['', Validators.required],
      email: ['', [Validators.required, Validators.pattern('[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$')]],
      phoneNumber: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],

      organizationName: ['', Validators.required],
      website: ['', [Validators.pattern('https?://.+')]],
      // state: [null, Validators.required],
    });
  }

  constructor(
    private superAdminService: SuperAdminService,
    private messageService: MessageService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.populateDropdownFromEnum();
    this.getAdmins(1);

    this.genders = [
      { name: 'Male', value: 1 },
      { name: 'Female', value: 2 },
      { name: 'Other', value: 3 }
    ]
  }

  onAdminStatusChange(event: ToggleButtonChangeEvent, admin: AdminResponse){
    
    const newStatus = event.checked ? AdminState.Active: AdminState.Inactive;
    this.editAdmin(admin, newStatus)
  }

  showDialog(admin?: AdminResponse){
    this.isVisible = true;
    if(admin){
    this.adminToEdit = admin;
    this.populateEditForm();
    }else{
      this.adminToEdit = null;
      this.newAdminForm.reset();
    }
  }
  closeDialog() {
    this.isVisible = false;
  }

  populateEditForm(){
    
    if(this.adminToEdit){
      this.newAdminForm.patchValue({
        firstName: this.adminToEdit.firstName,
        lastName: this.adminToEdit.lastName,
        gender: this.adminToEdit.gender,
        email: this.adminToEdit.email,
        phoneNumber: this.adminToEdit.phoneNumber,
        imageUrl: this.adminToEdit.imageUrl,
        organizationName: this.adminToEdit.organizationName,
        website: this.adminToEdit.website,
        state: this.adminToEdit.state,
      })
    }
  }

  populateDropdownFromEnum(){
    this.adminStateOptions = Object.keys(AdminState).filter(key => isNaN(Number(key))).map(key =>({
      name: key,
      value: AdminState[key as keyof typeof AdminState]
    }));
  }

  genderDescriptions(arg0: Gender | undefined): string {
    if (arg0) {
      return GenderDescriptions[arg0];
    }
    else {
      return '';
    }
  }

  upsertAdmin()
  {
    // if(this.adminToEdit){
    //   this.editAdmin();
    // }else{
    //   this.addAdmin();
    // }
  }

  editAdmin(admin: AdminResponse, newStatus: AdminState){
    
    console.log(admin);
    console.log(newStatus);
    this.isLoading = true;
    this.superAdminService.editAdmin({
      adminId: admin.applicationUserId,
      adminState: newStatus
    }).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.closeDialog();
        this.getAdmins(1);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Admin updated successfully' });
      },
      error:(err) =>{
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error updating admin' });
      }
    })
  }

  getAdmins(pageNumber: Number){
    this.isLoading = true;
    const getAdminRequest: GetAdminRequest = {
      organisationName: this.filterForm.value.organization,
      adminState: this.filterForm.value.adminState,
      startDate: this.filterForm.value.fromDate,
      endDate: this.filterForm.value.toDate,
      pageNumber: pageNumber,
      pageSize: this.rowSize
    }
    this.superAdminService.listAdmins(getAdminRequest).subscribe({
      next: (response) =>{
        if(response){
          this.adminList = response.result;
          this.totalRecords = response.total;
          this.isLoading = false;
        }else{
          this.adminList = [];
          this.totalRecords = 0;
          this.isLoading = false;
        }
      },
      error: (err) =>{
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching admins' });
      }
    })
  }

  addAdmin(){
    this.isLoading = true;
    this.submitted = true;
    this.newAdminForm.markAllAsTouched();
    if (this.newAdminForm?.invalid) {
      console.log(this.newAdminForm);
      this.isLoading = false;
      return;
    }
    const formData = this.newAdminForm.value;
    const adminData: RegisterAdminRequest = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      gender: formData.gender.value,//removed value
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      imageUrl: this.attachment ,
      organizationName: formData.organizationName,
      website: formData.website,
      state: AdminState.Active
    }
    this.superAdminService.addAdmin(adminData).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.submitted = false;
        if(response){
          this.adminList.push(response);
          this.fileUploader.clear();
          this.attachment = undefined;
          this.newAdminForm.reset();
          this.closeDialog();
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Admin added successfully' });
        }else{
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error adding admin' });
        }

      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error adding admin' });
      }
    })
  }

  onFileSelect($event: FileSelectEvent): void {
      if ($event.files && $event.files.length > 0) {
        // Convert files to an array
        const filesArray = Array.from($event.files);
        var index = 0;
        filesArray.forEach(item => {
          if (item.size > 1048576) { // 1 MB in bytes
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: `${item.name} file size can't exceed 1 MB`
            });
            filesArray.splice(index, 1);
            this.fileUploader.clear();
          }
          index++;
        });
        if (filesArray.length > 0) {
          this.attachment = filesArray[0];
        }
      }
    }

    onFileRemoved() {
      this.attachment = undefined;
      if (this.fileUploader) {
        this.fileUploader.clear();   // ✅ This clears the UI preview
      }
      this.cdr.detectChanges();      // Force UI refresh
    }

  onLazyLoad($event: TableLazyLoadEvent): void {
    const pageNumber = Math.floor(($event.first ?? 0) / ($event.rows ?? this.rowSize)) + 1;
    this.first = $event.first ?? 0;
    this.rowSize = $event.rows ?? this.rowSize;
    this.getAdmins(pageNumber);
  }
}
